/* Document expiry table (Documents page + employee profile Documents tab). Port of prototype docTable(). */
import { useState } from "react";
import type { Employee } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Button, DualDate, EmptyState, ExpiryChip, Icon, IconButton, Person, useToast } from "../../../ui";
import { RenewalGuideDrawer } from "./RenewalGuide";
import RenewModal, { type RenewTarget } from "./RenewModal";
import { docIcon } from "./shared";

export interface DocRow { e: Employee; kind: string; number: string; expiry: string; daysLeft: number }

export default function DocTable({ rows, withEmp, today, canRenew, testId = "doc-table" }: { rows: DocRow[]; withEmp: boolean; today: string; canRenew: boolean; testId?: string }) {
  const { t, L, name, nat } = useI18n();
  const toast = useToast();
  const [guide, setGuide] = useState<string | null>(null);
  const [renew, setRenew] = useState<RenewTarget | null>(null);

  if (!rows.length) return <EmptyState title={L("Nothing expiring in this window", "لا توجد وثائق تنتهي في هذه الفترة")} desc={L("Widen the window to see more documents.", "وسّع الفترة لعرض المزيد.")} />;

  return (
    <>
      <div className="tw">
        <table className="tbl cards" data-testid={testId}>
          <thead>
            <tr>
              {withEmp && <th>{t("Employee")}</th>}
              <th>{t("Document")}</th>
              <th className="hide-md">{t("Number")}</th>
              <th>{t("Expiry")}</th>
              <th className="end">{t("Days left")}</th>
              <th className="end">{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => {
              const key = x.e.id + "-" + x.kind;
              const slug = x.e.id + "-" + x.kind.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <tr key={key} data-testid={"doc-row-" + slug}>
                  {withEmp && <td className="k1"><Person e={x.e} meta={nat(x.e.nationality)} /></td>}
                  <td className={withEmp ? "k3" : "k1"} data-l={t("Document")}><span className="row nw"><Icon name={docIcon(x.kind)} className="s" />{t(x.kind)}</span></td>
                  <td className="hide-md num sm">{x.number}</td>
                  <td className="x" data-l={t("Expiry")}><DualDate date={x.expiry} /></td>
                  <td className="end k2"><ExpiryChip date={x.expiry} today={today} /></td>
                  <td className="end k3" data-l="">
                    <div className="row nw" style={{ justifyContent: "flex-end" }}>
                      <Button variant="ghost sm" icon="book" onClick={() => setGuide(x.kind)} data-testid={"btn-guide-" + slug}>{L("How to renew", "طريقة التجديد")}</Button>
                      {x.daysLeft <= 90 ? (
                        <>
                          <Button variant="ghost sm" onClick={() => toast(L("Reminder sent to " + x.e.nameEn + " by SMS", "تم إرسال تذكير إلى " + name(x.e)))} data-testid={"btn-remind-" + slug}>{t("Remind employee")}</Button>
                          {canRenew && <Button variant="sm" onClick={() => setRenew({ employeeId: x.e.id, employeeName: name(x.e), kind: x.kind, expiry: x.expiry })} data-testid={"btn-renew-" + slug}>{t("Renew")}</Button>}
                        </>
                      ) : (
                        canRenew && <IconButton icon="refresh" label={t("Renew")} onClick={() => setRenew({ employeeId: x.e.id, employeeName: name(x.e), kind: x.kind, expiry: x.expiry })} data-testid={"btn-renew-" + slug} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {guide && <RenewalGuideDrawer kind={guide} onClose={() => setGuide(null)} />}
      {renew && <RenewModal target={renew} today={today} onClose={() => setRenew(null)} />}
    </>
  );
}
